import { GridRenderCellParams } from "@mui/x-data-grid";
import { formatNumber, fPercent } from "src/utils/format-number";
import { ColumnFeatures } from "../../features";
import { palette } from "../../../figma-palette";
import { LightTooltip } from "../../tooltips/light";
import { Chip, Stack, Typography } from "@mui/material";
import useLocales from "src/locales/use-locales";
import { formatCurrency } from "src/utils/format-currency";
import { useCurrentWorkspace } from "src/context/reducers/app-settings";
import { WorkspaceList } from "src/context/api/workspaces";
import { isEmptyValue } from "./visibility";
import { isNegativeDiffGood, isPositiveDiffGood } from "./diff";
import { CompareKind } from "src/components/data-grid/compare-kind";
import { renderMetricCell } from "src/components/data-grid/data-grid-performance-split-business-overview";
import { pageMax } from "../../inline-evolution";
import { getGridStringOperators } from "@mui/x-data-grid-pro";
import PopperCell from "../../popper-cell";
import VisibilityRateTooltip from "../../tooltips/visibility-rate";
import Iconify from "src/components/iconify";

// NB: every export of this module is iterated as a grid column; keep helpers non-exported or in another module.
export { default as search_term } from "./search-term";

/** Competitor columns hold domains: text operators, matched server-side on each competitor's domain. */
const COMPETITOR_FILTER_OPERATORS = ['contains', 'doesNotContain', 'equals', 'doesNotEqual', 'startsWith', 'endsWith'].map(
    (value) => getGridStringOperators().find((operator) => operator.value === value)!
);

/** Hover state of the visibility rate breakdown popovers (one open at a time, like the split cells). */
const visibilityPopperRef = { current: { rowId: null as string | null } };

/**
 * Value + bar columns of the default view, 196px wide in the maquette: the min width leaves room for the
 * inline evolution (added by useBuildColumns) in a 1440px screen, flex grows them in the maquette proportions.
 */
const METRIC_SIZE = { minWidth: 100, flex: 1.96 };

/** Shown instead of a value when a ratio is undefined (e.g. 0 conversions → SAFE_DIVIDE NULL). */
const NULL_VALUE = '–';

/** Value + bar sized against the max of the page (Explore look). The evolution is appended by useBuildColumns. */
const metricWithBar = (params: GridRenderCellParams, field: string, displayValue: string, barColor: string) => {
    const value = params.row[field];
    return renderMetricCell({
        displayValue,
        barValue: Number(value) || 0,
        // Max registered once per data set by the grid (no per-cell scan of the rows).
        max: pageMax(params.row, field),
        barColor,
        showCompare: false,
        valueAlign: 'left',
        // Widest values: "999.9K", "€20.4K" (54px); the default 70px slot left the bar ~8px at 1440px
        valueWidth: 56,
    });
};

export const monthly_search_volumes = {
    field: 'monthly_search_volumes',
    position: 1,
    compareKind: 'number' as CompareKind,
    headerName: 'dashboard.nb_brand_grid.columns.title.monthly_search_volumes',
    headerTooltip: 'dashboard.nb_brand_grid.columns.tooltip.monthly_search_volumes',
    cellClassName: 'column--data-column',
    disableColumnMenu: true,
    format: formatNumber,
    type: 'number',
    features: [ColumnFeatures.COMPARISON, ColumnFeatures.DIFF],
    diff: {
        good: isPositiveDiffGood,
    },
    ...METRIC_SIZE,
    renderCell: (params: GridRenderCellParams) =>
        // null = no Keyword Planner volume yet for the period (latest months land late), not 0 searches.
        params.row.monthly_search_volumes === null || params.row.monthly_search_volumes === undefined
            ? NULL_VALUE
            : metricWithBar(params, 'monthly_search_volumes', formatNumber(params.row.monthly_search_volumes), '#F08C76'),
}

export const visibility_rate = {
    field: 'visibility_rate',
    isEmpty: (row: any) => isEmptyValue(row.visibility_rate),
    position: 8,
    compareKind: 'percent' as CompareKind,
    headerName: 'dashboard.nb_brand_grid.columns.title.visibility_rate',
    headerTooltip: 'dashboard.nb_brand_grid.columns.tooltip.visibility_rate',
    minWidth: 56,
    flex: 1.32,
    color: palette.visibility,
    format: fPercent,
    type: 'number',
    features: [ColumnFeatures.COMPARISON, ColumnFeatures.DIFF],
    diff: {
        good: isPositiveDiffGood,
    },
    // Value only, no bar (the evolution is appended by useBuildColumns); the breakdown shows on hover
    renderCell: (params: GridRenderCellParams) => (
        <PopperCell
            popperRef={visibilityPopperRef}
            rowId={params.row.id}
            justifyContent="flex-start"
            popper={
                <VisibilityRateTooltip
                    visibilityRate={params.row.visibility_rate}
                    organic={params.row.organic_visibility_rate}
                    paid={params.row.paid_visibility_rate}
                />
            }
        >
            <Typography variant="body2">{fPercent(params.row.visibility_rate)}</Typography>
        </PopperCell>
    ),
}


export const organic_visibility_rate = {
    field: 'organic_visibility_rate',
    isEmpty: (row: any) => isEmptyValue(row.organic_visibility_rate),
    position: 11,
    compareKind: 'percent' as CompareKind,
    // Own short label: organic_visibility_rate is shared with the Competitors grid
    headerName: 'dashboard.nb_brand_grid.columns.title.organic_vr',
    headerTooltip: 'dashboard.nb_brand_grid.columns.tooltip.organic_visibility_rate',


    color: palette.visibility,
    format: fPercent,
    type: 'number',
    features: [ColumnFeatures.COMPARISON, ColumnFeatures.DIFF, ColumnFeatures.SIMPLEBAR],
    diff: {
        good: isPositiveDiffGood,
    },
    renderCell: (params: GridRenderCellParams) => {
        const color = palette.organicRange.find(
            ({ min, max }) => {
                return params.row.organic_visibility_rate >= min && params.row.organic_visibility_rate <= max
            }
        )?.color;

        return (
            <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                py={1}
                sx={{ width: '100%', height: '100%' }}
            >
                <Typography variant="body2">
                    {fPercent(params.row.organic_visibility_rate)}
                </Typography>
                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    py={1}
                    sx={{ width: '100%', height: '100%' }}
                >
                    <Stack
                        sx={{
                            width: `${params.row.organic_visibility_rate}%`,
                            height: '8px',
                            backgroundColor: color,
                            borderRadius: '6px',
                        }}
                    />
                </Stack>
            </Stack>
        );
    },
}

export const paid_search_visibility_rate = {
    field: 'paid_search_visibility_rate',
    isEmpty: (row: any) => isEmptyValue(row.paid_search_visibility_rate),
    position: 9,
    compareKind: 'percent' as CompareKind,
    headerName: 'dashboard.nb_brand_grid.columns.title.paid_search_visibility_rate',

    color: palette.visibility,
    format: fPercent,
    type: 'number',
    features: [ColumnFeatures.COMPARISON, ColumnFeatures.DIFF, ColumnFeatures.SIMPLEBAR],
    diff: {
        good: isPositiveDiffGood,
    },
    renderCell: (params: GridRenderCellParams) => {
        const color = palette.paidRange.find(
            ({ min, max }) => {
                return params.row.paid_search_visibility_rate >= min && params.row.paid_search_visibility_rate <= max
            }
        )?.color;

        return (
            <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                py={1}
                sx={{ width: '100%', height: '100%' }}
            >
                <Typography variant="body2">
                    {fPercent(params.row.paid_search_visibility_rate)}
                </Typography>
                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    py={1}
                    sx={{ width: '100%', height: '100%' }}
                >
                    <Stack
                        sx={{
                            width: `${params.row.paid_search_visibility_rate}%`,
                            height: '8px',
                            backgroundColor: color,
                            borderRadius: '6px',
                        }}
                    />
                </Stack>
            </Stack>
        );
    },
}

export const paid_shopping_visibility_rate = {
    field: 'paid_shopping_visibility_rate',
    isEmpty: (row: any) => isEmptyValue(row.paid_shopping_visibility_rate),
    position: 10,
    compareKind: 'percent' as CompareKind,
    headerName: 'dashboard.nb_brand_grid.columns.title.paid_shopping_visibility_rate',


    color: palette.visibility,
    format: fPercent,
    type: 'number',
    features: [ColumnFeatures.COMPARISON, ColumnFeatures.DIFF, ColumnFeatures.SIMPLEBAR],
    diff: {
        good: isPositiveDiffGood,
    },
    renderCell: (params: GridRenderCellParams) => {
        const color = palette.paidRange.find(
            ({ min, max }) => {
                return params.row.paid_shopping_visibility_rate >= min && params.row.paid_shopping_visibility_rate <= max
            }
        )?.color;

        return (
            <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                py={1}
                sx={{ width: '100%', height: '100%' }}
            >
                <Typography variant="body2">
                    {fPercent(params.row.paid_shopping_visibility_rate)}
                </Typography>
                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    py={1}
                    sx={{ width: '100%', height: '100%' }}
                >
                    <Stack
                        sx={{
                            width: `${params.row.paid_shopping_visibility_rate}%`,
                            height: '8px',
                            backgroundColor: color,
                            borderRadius: '6px',
                        }}
                    />
                </Stack>
            </Stack>
        );
    },
}


export const top5_compet_total = {
    field: 'top5_compet_total',
    // Array of structs: BigQuery cannot ORDER BY it.
    sortable: false,
    filterOperators: COMPETITOR_FILTER_OPERATORS,
    position: 2,
    headerName: 'dashboard.nb_brand_grid.columns.title.top5_compet_total',
    headerTooltip: 'dashboard.nb_brand_grid.columns.tooltip.top5_compet_total',
    // "Top Competitors" title: 109px + the header padding
    minWidth: 130,
    flex: 1.2,
    transform: (data: any) => (data.map((item: any) => Object.assign({}, item, { rate_field: item.total_rate }))),

    type: 'number',
    features: [ColumnFeatures.COMPARISON, ColumnFeatures.RANK],
}

export const top5_search_rate = {
    field: 'top5_search_rate',
    // Array of structs: BigQuery cannot ORDER BY it.
    sortable: false,
    filterOperators: COMPETITOR_FILTER_OPERATORS,
    position: 3,
    // No paid search data (empty Paid Search SoV): no competitors to show either
    isEmpty: (row: any) => isEmptyValue(row.paid_search_share?.paid_search_visibility_rate),
    headerName: 'dashboard.nb_brand_grid.columns.title.top5_search_rate',
    headerTooltip: 'dashboard.nb_brand_grid.columns.tooltip.top5_search_rate',


    type: 'number',
    features: [ColumnFeatures.COMPARISON, ColumnFeatures.RANK],
    transform: (data: any) => (data.map((item: any) => Object.assign({}, item, { rate_field: item.search_rate }))),
}

export const top5_shopping_rate = {
    field: 'top5_shopping_rate',
    // Array of structs: BigQuery cannot ORDER BY it.
    sortable: false,
    filterOperators: COMPETITOR_FILTER_OPERATORS,
    position: 4,
    // No paid shopping data (empty Paid Shopping SoV): no competitors to show either
    isEmpty: (row: any) => isEmptyValue(row.paid_shopping_share?.paid_shopping_visibility_rate),
    headerName: 'dashboard.nb_brand_grid.columns.title.top5_shopping_rate',
    headerTooltip: 'dashboard.nb_brand_grid.columns.tooltip.top5_shopping_rate',
    transform: (data: any) => (data.map((item: any) => Object.assign({}, item, { rate_field: item.shopping_rate }))),

    type: 'number',
    features: [ColumnFeatures.COMPARISON, ColumnFeatures.RANK],
}

export const top5_organic_rate = {
    field: 'top5_organic_rate',
    // Array of structs: BigQuery cannot ORDER BY it.
    sortable: false,
    filterOperators: COMPETITOR_FILTER_OPERATORS,
    position: 5,
    // No organic data (empty Organic SoV): no competitors to show either
    isEmpty: (row: any) => isEmptyValue(row.organic_share?.organic_share_rate),
    headerName: 'dashboard.nb_brand_grid.columns.title.top5_organic_rate',
    headerTooltip: 'dashboard.nb_brand_grid.columns.tooltip.top5_organic_rate',
    transform: (data: any) => (data.map((item: any) => Object.assign({}, item, { rate_field: item.organic_rate }))),
    type: 'number',
    features: [ColumnFeatures.COMPARISON, ColumnFeatures.RANK],
}

export const avg_seo_position = {
    field: 'avg_seo_position',
    position: 12,
    compareKind: 'number' as CompareKind,
    headerName: 'dashboard.nb_brand_grid.columns.title.avg_seo_position',
    headerTooltip: 'dashboard.nb_brand_grid.columns.tooltip.avg_seo_position',
    headerTooltipLink: 'dashboard.nonBrand.tooltips.avgSeoPositionLink',
    headerTooltipLinkLabel: 'dashboard.nb_brand_grid.columns.tooltip.learn_more',
    type: 'number',
    features: [ColumnFeatures.COMPARISON, ColumnFeatures.DIFF],
    // Positive evolution in green, as in the maquette (recette 2026-10-22), although a lower position is better
    diff: {
        good: isPositiveDiffGood,
    },
}

export const ad_cost = {
    field: 'ad_cost',
    position: 19,
    compareKind: 'currency' as CompareKind,
    ...METRIC_SIZE,
    headerName: 'dashboard.nb_brand_grid.columns.title.ad_cost',
    headerTooltip: 'dashboard.nb_brand_grid.columns.tooltip.ad_cost',


    type: 'number',
    features: [ColumnFeatures.COMPARISON, ColumnFeatures.DIFF],
    diff: {
        good: isNegativeDiffGood,
    },
    renderCell: (params: GridRenderCellParams) => {
        const { currency } = useCurrentWorkspace() as WorkspaceList;
        const { currentLang } = useLocales();
        const locale = currentLang.value || 'en-US';
        return metricWithBar(params, 'ad_cost', formatCurrency(params.row.ad_cost, currency, locale, true, 0), '#D9A43B');
    }
}

export const ad_cpc = {
    field: 'ad_cpc',
    position: 23,
    compareKind: 'currency' as CompareKind,
    headerName: 'dashboard.nb_brand_grid.columns.title.ad_cpc',
    headerTooltip: 'dashboard.nb_brand_grid.columns.tooltip.ad_cpc',


    type: 'number',
    features: [ColumnFeatures.COMPARISON, ColumnFeatures.DIFF],
    diff: {
        good: isNegativeDiffGood,
    },
    renderCell: (params: GridRenderCellParams) => {
        const { currency } = useCurrentWorkspace() as WorkspaceList;
        const { currentLang } = useLocales();
        const locale = currentLang.value || 'en-US';
        return formatCurrency(params.row.ad_cpc, currency, locale, true, 2);
    }
}

export const paid_roas = {
    field: 'paid_roas',
    position: 20,
    compareKind: 'number' as CompareKind,
    headerName: 'dashboard.nb_brand_grid.columns.title.paid_roas',
    headerTooltip: 'dashboard.nb_brand_grid.columns.tooltip.paid_roas',
    minWidth: 70,
    flex: 1.2,
    type: 'number',
    features: [ColumnFeatures.COMPARISON, ColumnFeatures.DIFF],
    diff: {
        good: isPositiveDiffGood,
    },
}

export const paid_aov = {
    field: 'paid_aov',
    position: 21,
    compareKind: 'currency' as CompareKind,
    headerName: 'dashboard.nb_brand_grid.columns.title.paid_aov',
    headerTooltip: 'dashboard.nb_brand_grid.columns.tooltip.paid_aov',
    cellClassName: 'column--data-column',
    disableColumnMenu: true,
    type: 'number',
    features: [ColumnFeatures.COMPARISON, ColumnFeatures.DIFF],
    diff: {
        good: isPositiveDiffGood,
    },
    renderCell: (params: GridRenderCellParams) => {
        const { currency } = useCurrentWorkspace() as WorkspaceList;
        const { currentLang } = useLocales();
        const locale = currentLang.value || 'en-US';
        // Hooks above stay unconditional; null = undefined ratio (0 conversions), not 0.
        if (params.row.paid_aov === null || params.row.paid_aov === undefined) return NULL_VALUE;
        return formatCurrency(params.row.paid_aov, currency, locale, true, 2);
    }
}

export const paid_cpa = {
    field: 'paid_cpa',
    position: 22,
    compareKind: 'currency' as CompareKind,
    headerName: 'dashboard.nb_brand_grid.columns.title.paid_cpa',
    headerTooltip: 'dashboard.nb_brand_grid.columns.tooltip.paid_cpa',
    minWidth: 80,
    flex: 1.2,
    type: 'number',
    features: [ColumnFeatures.COMPARISON, ColumnFeatures.DIFF],
    diff: {
        good: isNegativeDiffGood,
    },
    renderCell: (params: GridRenderCellParams) => {
        const { currency } = useCurrentWorkspace() as WorkspaceList;
        const { currentLang } = useLocales();
        const locale = currentLang.value || 'en-US';
        // Hooks above stay unconditional; null = undefined ratio (0 conversions), not 0.
        if (params.row.paid_cpa === null || params.row.paid_cpa === undefined) return NULL_VALUE;
        return formatCurrency(params.row.paid_cpa, currency, locale, true, 2);
    }
}
export const cvr = {
    field: 'cvr',
    position: 26,
    compareKind: 'percent' as CompareKind,
    headerName: 'dashboard.nb_brand_grid.columns.title.cvr',
    headerTooltip: 'dashboard.nb_brand_grid.columns.tooltip.cvr',


    type: 'number',
    features: [ColumnFeatures.COMPARISON, ColumnFeatures.DIFF],
    diff: {
        good: isPositiveDiffGood,
    },
    renderCell: (params: GridRenderCellParams) => {
        return fPercent(params.row.cvr);
    },
}
export const ctr = {
    field: 'blended_ctr',
    position: 14,
    compareKind: 'percent' as CompareKind,
    headerName: 'dashboard.nb_brand_grid.columns.title.ctr',
    headerTooltip: 'dashboard.nb_brand_grid.columns.tooltip.ctr',
    type: 'number',
    features: [ColumnFeatures.COMPARISON, ColumnFeatures.DIFF],
    ...METRIC_SIZE,
    diff: {
        good: isPositiveDiffGood,
    },
    renderCell: (params: GridRenderCellParams) =>
        // null = no searches (SAFE_DIVIDE NULL): a dash without a bar, not 0%.
        params.row.blended_ctr === null || params.row.blended_ctr === undefined
            ? NULL_VALUE
            : metricWithBar(params, 'blended_ctr', fPercent(params.row.blended_ctr), palette.blended.ctr),
}
export const organic_ctr = {
    field: 'organic_ctr',
    position: 15,
    compareKind: 'percent' as CompareKind,
    headerName: 'dashboard.nb_brand_grid.columns.title.organic_ctr',
    headerTooltip: 'dashboard.nb_brand_grid.columns.tooltip.organic_ctr',
    type: 'number',
    features: [ColumnFeatures.COMPARISON, ColumnFeatures.DIFF],
    diff: {
        good: isPositiveDiffGood,
    },
    renderCell: (params: GridRenderCellParams) => {
        return fPercent(params.row.organic_ctr);
    },
}
export const paid_search_ctr = {
    field: 'paid_search_ctr',
    position: 16,
    compareKind: 'percent' as CompareKind,
    headerName: 'dashboard.nb_brand_grid.columns.title.paid_search_ctr',
    headerTooltip: 'dashboard.nb_brand_grid.columns.tooltip.paid_search_ctr',
    type: 'number',
    features: [ColumnFeatures.COMPARISON, ColumnFeatures.DIFF],
    diff: {
        good: isPositiveDiffGood,
    },
    renderCell: (params: GridRenderCellParams) => {
        return fPercent(params.row.paid_search_ctr);
    },
}
export const paid_shopping_ctr = {
    field: 'paid_shopping_ctr',
    position: 17,
    compareKind: 'percent' as CompareKind,
    headerName: 'dashboard.nb_brand_grid.columns.title.paid_shopping_ctr',
    headerTooltip: 'dashboard.nb_brand_grid.columns.tooltip.paid_shopping_ctr',


    type: 'number',
    features: [ColumnFeatures.COMPARISON, ColumnFeatures.DIFF],
    diff: {
        good: isPositiveDiffGood,
    },
    renderCell: (params: GridRenderCellParams) => {
        return fPercent(params.row.paid_shopping_ctr);
    },
}

export const clicks_split = {
    field: 'clicks_split',
    position: 18,
    compareKind: 'number' as CompareKind,
    compareField: 'total_clicks',
    headerName: 'dashboard.nb_brand_grid.columns.title.clicks_split',
    headerTooltip: 'dashboard.nb_brand_grid.columns.tooltip.clicks_split',
    ...METRIC_SIZE,
    type: 'number',
    features: [ColumnFeatures.COMPARISON, ColumnFeatures.SPLIT],
    format: formatNumber,
    // Maquette: the traffic of each source, without the split bar (the cell already draws it)
    tooltip: { showBar: false, values: 'count' as const },
    transform: (params: GridRenderCellParams) => ({
        total: params.row.clicks_split.total_clicks,
        data: [
            {
                name: 'dashboard.nb_brand_grid.columns.title.organic',
                value: params.row.clicks_split.percent_organic_clicks,
                help: params.row.clicks_split.organic_clicks,
                color: palette.organic,
                format: fPercent,
            },
            {
                name: 'PMax',
                value:
                    params.row.clicks_split.total_clicks > 0
                        ? Math.round(
                            (params.row.clicks_split.pmax_clicks /
                                params.row.clicks_split.total_clicks) * 100
                        )
                        : 0,
                help: params.row.clicks_split.pmax_clicks,
                color: palette.pmax,
                format: fPercent,
            },
            {
                name: 'dashboard.nb_brand_grid.columns.title.paid_search',
                value: params.row.clicks_split.percent_ad_clicks_from_search,
                help: params.row.clicks_split.paid_search_clicks,
                color: palette.paid.search,
                format: fPercent,
            },
            {
                name: 'dashboard.nb_brand_grid.columns.title.paid_shopping',
                value: params.row.clicks_split.percent_ad_clicks_from_shopping,
                help: params.row.clicks_split.paid_shopping_clicks,
                color: palette.paid.shopping,
                format: fPercent,
            },
        ],
    }),
}

export const organic_clicks = {
    field: 'organic_clicks',
    position: null,
    visibility: null,
    headerName: 'dashboard.nb_brand_grid.columns.title.organic_clicks',
    headerTooltip: 'dashboard.nb_brand_grid.columns.tooltip.organic_clicks',
    type: 'number',
    features: [ColumnFeatures.COMPARISON, ColumnFeatures.SPLIT],
    format: formatNumber,
}

export const paid_search_clicks = {
    field: 'paid_search_clicks',
    position: null,
    visibility: null,
    headerName: 'dashboard.nb_brand_grid.columns.title.paid_search_clicks',
    headerTooltip: 'dashboard.nb_brand_grid.columns.tooltip.paid_search_clicks',
    type: 'number',
    features: [ColumnFeatures.COMPARISON, ColumnFeatures.SPLIT],
    format: formatNumber,
}

export const paid_shopping_clicks = {
    field: 'paid_shopping_clicks',
    position: null,
    visibility: null,
    headerName: 'dashboard.nb_brand_grid.columns.title.paid_shopping_clicks',
    headerTooltip: 'dashboard.nb_brand_grid.columns.tooltip.paid_shopping_clicks',
    type: 'number',
    features: [ColumnFeatures.COMPARISON, ColumnFeatures.SPLIT],
    format: formatNumber,
}


export const paid_search_share = {
    field: 'paid_search_share',
    isEmpty: (row: any) => isEmptyValue(row.paid_search_share?.paid_search_visibility_rate),
    position: 6,
    compareKind: 'percent' as CompareKind,
    // The displayed share (ads SERPs denominator), not the top-level paid_search_visibility_rate (all SERPs).
    compareField: 'paid_search_share.paid_search_visibility_rate',
    headerName: 'dashboard.nb_brand_grid.columns.title.paid_search_sov',
    headerTooltip: 'dashboard.nb_brand_grid.columns.tooltip.paid_search_share',


    type: 'number',
    features: [ColumnFeatures.COMPARISON, ColumnFeatures.SPLIT],
    format: fPercent,
    transform: (params: GridRenderCellParams) => ({
        total: params.row.paid_search_share.paid_search_visibility_rate,
        data: [
            {
                name: 'dashboard.nb_brand_grid.columns.competitors.1st_position',
                value: params.row.paid_search_share.first_search_visibility_rate,
                color: palette.paidSearchShare.first,
            },
            {
                name: 'dashboard.nb_brand_grid.columns.competitors.2-3_position',
                value: params.row.paid_search_share.top_search_visibility_rate,
                color: palette.paidSearchShare.then,
            },
            {
                name: 'dashboard.nb_brand_grid.columns.competitors.competitors',
                value: params.row.paid_search_share.compet_search_visibility_rate,
                color: palette.paidSearchShare.compet,
            },
        ],
    }),
}
export const paid_shopping_share = {
    field: 'paid_shopping_share',
    isEmpty: (row: any) => isEmptyValue(row.paid_shopping_share?.paid_shopping_visibility_rate),
    position: 7,
    compareKind: 'percent' as CompareKind,
    compareField: 'paid_shopping_share.paid_shopping_visibility_rate',
    headerName: 'dashboard.nb_brand_grid.columns.title.paid_shopping_sov',
    headerTooltip: 'dashboard.nb_brand_grid.columns.tooltip.paid_shopping_share',


    type: 'number',
    features: [ColumnFeatures.COMPARISON, ColumnFeatures.SPLIT],
    format: fPercent,
    transform: (params: GridRenderCellParams) => ({
        total: params.row.paid_shopping_share.paid_shopping_visibility_rate,
        data: [
            {
                name: 'dashboard.nb_brand_grid.columns.competitors.1-3_position',
                value: params.row.paid_shopping_share.last_shopping_visibility_rate,
                color: palette.paidShoppingShare.first,
            },
            {
                name: 'dashboard.nb_brand_grid.columns.competitors.4-10_position',
                value: params.row.paid_shopping_share.top_shopping_visibility_rate,
                color: palette.paidShoppingShare.then,
            },
            {
                name: 'dashboard.nb_brand_grid.columns.competitors.competitors',
                value: params.row.paid_shopping_share.compet_shopping_visibility_rate,
                color: palette.paidShoppingShare.compet,
            },
        ],
    }),
}
export const organic_share = {
    field: 'organic_share',
    isEmpty: (row: any) => isEmptyValue(row.organic_share?.organic_share_rate),
    // Right after Paid Shopping SoV (7), before Visibility rate (8)
    position: 7.5,
    compareKind: 'percent' as CompareKind,
    compareField: 'organic_share.organic_share_rate',
    headerName: 'dashboard.nb_brand_grid.columns.title.organic_sov',
    headerTooltip: 'dashboard.nb_brand_grid.columns.tooltip.organic_share',
    type: 'number',
    features: [ColumnFeatures.COMPARISON, ColumnFeatures.SPLIT],
    format: fPercent,
    transform: (params: GridRenderCellParams) => ({
        total: params.row.organic_share.organic_share_rate,
        data: [
            {
                name: 'dashboard.nb_brand_grid.columns.competitors.1st_position',
                value: params.row.organic_share.first_organic_share_rate,
                color: palette.organicShare.first,
            },
            {
                name: 'dashboard.nb_brand_grid.columns.competitors.2-3_position',
                value: params.row.organic_share.top_organic_share_rate,
                color: palette.organicShare.then,
            },
            {
                name: 'dashboard.nb_brand_grid.columns.competitors.competitors',
                value: params.row.organic_share.compet_organic_share_rate,
                color: palette.organicShare.compet,
            },
        ],
    }),
}
export const conversions = {
    field: 'conversions',
    position: 24,
    compareKind: 'number' as CompareKind,
    compareField: 'conversions.total_conversions',
    headerName: 'dashboard.nb_brand_grid.columns.title.non_brand_conversions',
    headerTooltip: 'dashboard.nb_brand_grid.columns.tooltip.conversions',
    type: 'number',
    features: [ColumnFeatures.COMPARISON, ColumnFeatures.SPLIT],
    diff: {
        good: isPositiveDiffGood,
    },
    format: formatNumber,
    // Same tooltip as Non brand traffic: the conversions of each source, without the split bar
    tooltip: { showBar: false, values: 'count' as const },
    transform: (params: GridRenderCellParams) => {
        const { organic_conversions, pmax_conversions, paid_search_conversions, paid_shopping_conversions } = params.row.conversions;
        const total = organic_conversions + paid_search_conversions + paid_shopping_conversions + pmax_conversions;
        const share = (value: number) => (total > 0 ? Math.round((value / total) * 100) : 0);

        return {
            total,
            data: [
                { name: 'dashboard.nb_brand_grid.columns.title.organic', value: share(organic_conversions), help: organic_conversions, color: palette.organic, format: fPercent },
                { name: 'PMax', value: share(pmax_conversions), help: pmax_conversions, color: palette.pmax, format: fPercent },
                { name: 'dashboard.nb_brand_grid.columns.title.paid_search', value: share(paid_search_conversions), help: paid_search_conversions, color: palette.paid.search, format: fPercent },
                { name: 'dashboard.nb_brand_grid.columns.title.paid_shopping', value: share(paid_shopping_conversions), help: paid_shopping_conversions, color: palette.paid.shopping, format: fPercent },
            ],
        };
    },
}

export const organic_conversions = {
    field: 'organic_conversions',
    position: null,
    visibility: null,
    toggleable: false,
    headerName: 'dashboard.nb_brand_grid.columns.title.organic_conversions',
    headerTooltip: 'dashboard.nb_brand_grid.columns.tooltip.organic_conversions',
    type: 'number',
    features: [ColumnFeatures.COMPARISON, ColumnFeatures.SPLIT],
    format: formatNumber,
}
export const paid_search_conversions = {
    field: 'paid_search_conversions',
    position: null,
    visibility: null,
    headerName: 'dashboard.nb_brand_grid.columns.title.paid_search_conversions',
    headerTooltip: 'dashboard.nb_brand_grid.columns.tooltip.paid_search_conversions',
    type: 'number',
    features: [ColumnFeatures.COMPARISON, ColumnFeatures.SPLIT],
    format: formatNumber,
}
export const paid_shopping_conversions = {
    field: 'paid_shopping_conversions',
    position: null,
    visibility: null,
    headerName: 'dashboard.nb_brand_grid.columns.title.paid_shopping_conversions',
    headerTooltip: 'dashboard.nb_brand_grid.columns.tooltip.paid_shopping_conversions',
    type: 'number',
    features: [ColumnFeatures.COMPARISON, ColumnFeatures.SPLIT],
    format: formatNumber,
}

export const revenue = {
    field: 'revenue',
    position: 25,
    compareKind: 'currency' as CompareKind,
    compareField: 'revenue.revenue',
    headerName: 'dashboard.nb_brand_grid.columns.title.non_brand_revenue',
    headerTooltip: 'dashboard.nb_brand_grid.columns.tooltip.revenue',


    type: 'number',
    features: [ColumnFeatures.COMPARISON, ColumnFeatures.SPLIT],
    diff: {
        good: isPositiveDiffGood,
    },
    format: formatNumber,
    // Same tooltip as Non brand traffic: the revenue of each source, without the split bar
    tooltip: { showBar: false, values: 'count' as const },
    transform: (params: GridRenderCellParams) => {
        const { organic_revenue, pmax_revenue = 0, paid_search_revenue, paid_shopping_revenue } = params.row.revenue;
        return {
            total: params.row.revenue.revenue,
            data: [
                { name: 'dashboard.nb_brand_grid.columns.title.organic', value: organic_revenue, help: organic_revenue, color: palette.organic },
                { name: 'PMax', value: pmax_revenue, help: pmax_revenue, color: palette.pmax },
                { name: 'dashboard.nb_brand_grid.columns.title.paid_search', value: paid_search_revenue, help: paid_search_revenue, color: palette.paid.search },
                { name: 'dashboard.nb_brand_grid.columns.title.paid_shopping', value: paid_shopping_revenue, help: paid_shopping_revenue, color: palette.paid.shopping },
            ],
        };
    },
}
export const opportunity = {
    field: 'opportunity',
    position: 30,
    headerName: 'dashboard.nb_brand_grid.columns.title.opportunity',
    headerTooltip: 'dashboard.nb_brand_grid.columns.tooltip.opportunity',
    minWidth: 120,
    flex: 1.1,
    type: 'string',
    features: [ColumnFeatures.COMPARISON, ColumnFeatures.DIFF],
    renderCell: (params: GridRenderCellParams) => {
        const { t } = useLocales();
        const opportunityKey = params.row.opportunity.toLowerCase().replace(/ /g, '');
        // No opportunity: plain "--", no colored chip.
        if (opportunityKey === 'no_opportunity') {
            return <Typography variant="body2" color="text.secondary">--</Typography>;
        }
        // Short chip labels (the toolbar filter keeps the long ones).
        const opportunityMap: Record<string, string> = {
            seo_boost: t('dashboard.nb_brand_grid.opportunities.chip.organic'),
            sea_boost: t('dashboard.nb_brand_grid.opportunities.chip.paid'),
            missed: t('dashboard.nb_brand_grid.opportunities.chip.missed'),
        };
        const label =
            opportunityMap[opportunityKey.toLowerCase()] ||
            params.row.opportunity.charAt(0).toUpperCase() + params.row.opportunity.slice(1);
        // The definition of the opportunity shows on the (i) icon, 8px right of the chip
        return (
            <Stack direction="row" alignItems="center" sx={{ gap: '8px', height: '100%' }}>
                <Chip
                    label={label}
                    className={`opportunity-chip opportunity-chip-${params.row.opportunity}`}
                    size="small"
                />
                <LightTooltip title={t('dashboard.nb_brand_grid.tooltips.' + opportunityKey)}>
                    <Iconify icon="eva:info-outline" width={16} sx={{ flexShrink: 0, color: 'text.disabled', cursor: 'help' }} />
                </LightTooltip>
            </Stack>
        );
    }
}